/* eslint-disable max-lines */
import {
  ArrowLongUpIcon,
  ArrowsUpDownIcon
} from "@heroicons/react/24/outline";
import { FC } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import FormWrapper from "../../../components/ui/FormWrapper";
import Input from "../../../components/ui/input/Input";
import useCan from "../../../components/users/permissions/useCan";
import IPricing, {
  IPostPriceSchema,
  StationPoint
} from "../../../interfaces/endpoints/IPricing";
import { postPrice } from "../../../services/Pricing.service";
import { editTrip, getCurrentTrip } from "../../../services/Trips.service";
import { cx } from "../../../utils/classnames";
import { Inputs } from "./index";

interface PricingTableProps {
  oneWayData?: IPricing;
  roundTripData?: IPricing;
  tripParams: Inputs;
}

interface PriceRow {
  departure: StationPoint;
  destination: StationPoint;
  oneWayPrice: number;
  oneWayPriceId: number;
  roundTripPrice: number;
  roundTripPriceId: number;
}

const flattenPrices = (
  oneWayData?: IPricing,
  roundTripData?: IPricing
): PriceRow[] => {
  const rows: PriceRow[] = [];

  const roundTripMap = new Map<string, { price: number; priceId: number }>();
  if (roundTripData?.schedulePricing) {
    for (const entries of Object.values(roundTripData.schedulePricing)) {
      for (const entry of entries) {
        const key = `${entry.departure.id}-${entry.destination.id}`;
        roundTripMap.set(key, { price: entry.price, priceId: entry.priceId });
      }
    }
  }

  let primaryDepartureId: number | undefined;
  let primaryDestinationId: number | undefined;

  if (oneWayData?.schedulePricing) {
    const firstGroupEntries = Object.values(oneWayData.schedulePricing)[0];
    if (firstGroupEntries?.length) {
      primaryDepartureId = firstGroupEntries[0].departure.id;
      primaryDestinationId =
        firstGroupEntries[firstGroupEntries.length - 1].destination.id;
    }

    for (const entries of Object.values(oneWayData.schedulePricing)) {
      for (const entry of entries) {
        const key = `${entry.departure.id}-${entry.destination.id}`;
        const rt = roundTripMap.get(key);
        rows.push({
          departure: entry.departure,
          destination: entry.destination,
          oneWayPrice: entry.price,
          oneWayPriceId: entry.priceId,
          roundTripPrice: rt?.price || 0,
          roundTripPriceId: rt?.priceId || 0
        });
      }
    }
  }

  return rows.sort((a, b) => {
    const aIsPrimaryRoute =
      a.departure.id === primaryDepartureId &&
      a.destination.id === primaryDestinationId
        ? 0
        : 1;
    const bIsPrimaryRoute =
      b.departure.id === primaryDepartureId &&
      b.destination.id === primaryDestinationId
        ? 0
        : 1;
    if (aIsPrimaryRoute !== bIsPrimaryRoute)
      return aIsPrimaryRoute - bIsPrimaryRoute;

    const aIsPrimaryDeparture =
      a.departure.id === primaryDepartureId ? 0 : 1;
    const bIsPrimaryDeparture =
      b.departure.id === primaryDepartureId ? 0 : 1;
    if (aIsPrimaryDeparture !== bIsPrimaryDeparture)
      return aIsPrimaryDeparture - bIsPrimaryDeparture;

    const aHasBoth =
      a.oneWayPrice > 0 && a.roundTripPrice > 0 ? 0 : 1;
    const bHasBoth =
      b.oneWayPrice > 0 && b.roundTripPrice > 0 ? 0 : 1;
    if (aHasBoth !== bHasBoth) return aHasBoth - bHasBoth;

    const aHasAny =
      a.oneWayPrice > 0 || a.roundTripPrice > 0 ? 0 : 1;
    const bHasAny =
      b.oneWayPrice > 0 || b.roundTripPrice > 0 ? 0 : 1;
    if (aHasAny !== bHasAny) return aHasAny - bHasAny;

    return 0;
  });
};

const PricingTable: FC<PricingTableProps> = ({
  oneWayData,
  roundTripData,
  tripParams
}) => {
  const rows = flattenPrices(oneWayData, roundTripData);
  const { handleSubmit, register } = useForm({});
  const can = useCan("pricings.pricing.update");
  const currencyLabel = tripParams?.defaultCurrencyId === 2 ? "ALL" : "€";

  const updateTripCurrency = async () => {
    if (!tripParams?.tripId || !tripParams?.defaultCurrencyId) return;

    const response = await getCurrentTrip(tripParams.tripId);
    const tripData = response.data;

    // Only save the trip when the currency actually moved. Route/EditTrip rebuilds the trip
    // from whatever this payload carries, so firing it on every price save was enough to wipe
    // the working days, bump the audit fields and re-send the trip-changed notices.
    if (tripData.trip.defaultCurrencyId === tripParams.defaultCurrencyId) return;

    await editTrip({
      routeName: tripData.routeName,
      entityCommissionFee: tripData.entityCommissionFee,
      routeDescription: tripData.routeDescription,
      trip: {
        arrivalTime: tripData.trip.arrivalTimeString,
        departureTime: tripData.trip.departureTimeString,
        duration: Math.abs(tripData.trip.duration),
        isReservable: tripData.trip.isReservable,
        lineId: tripData.trip.lineId,
        id: tripData.trip.id,
        defaultCurrencyId: tripParams.defaultCurrencyId
      },
      operationalInterval: tripData.operationalInterval,
      // Hand the stored working days back untouched - this call is about the currency only.
      customWorkingDays: tripData.customWorkingDays,
      schedules: tripData.schedules.map((schedule) => ({
        tripId: schedule.tripId,
        stationId: schedule.stationId,
        arrivalTime: schedule.arrivalTimeString,
        departureTime: schedule.departureTimeString,
        duration: schedule.duration,
        id: 0
      })),
      prices: tripData.prices
    });
  };

  const onSubmit: SubmitHandler<any> = async (pricingData) => {
    const pricesArr: any[] = Object.values(pricingData.prices ?? {});

    try {
      await updateTripCurrency();
    } catch (error) {
      toast.error("Ndodhi një gabim gjatë ndryshimit të valutës.");
      return;
    }

    const isValidNumber = (v: unknown): v is number =>
      typeof v === "number" && !isNaN(v);

    const oneWayPrices: IPostPriceSchema["prices"] = pricesArr
      .filter(
        (p) => isValidNumber(p.oneWayPrice) || p.oneWayPriceId > 0
      )
      .map((p) => ({
        id: p.oneWayPriceId || 0,
        oneWayPrice: isValidNumber(p.oneWayPrice) ? p.oneWayPrice : 0,
        roundTripPrice: 0,
        tripId: p.tripId,
        ageGroupId: p.ageGroupId,
        stationFrom: p.stationFrom,
        stationTo: p.stationTo
      }));

    const roundTripPrices: IPostPriceSchema["prices"] = pricesArr
      .filter(
        (p) => isValidNumber(p.roundTripPrice) || p.roundTripPriceId > 0
      )
      .map((p) => ({
        id: p.roundTripPriceId || 0,
        oneWayPrice: 0,
        roundTripPrice: isValidNumber(p.roundTripPrice) ? p.roundTripPrice : 0,
        tripId: p.tripId,
        ageGroupId: p.ageGroupId,
        stationFrom: p.stationFrom,
        stationTo: p.stationTo
      }));

    if (oneWayPrices.length === 0 && roundTripPrices.length === 0) {
      toast.success("Valuta u ndryshua me sukses.", { position: "top-right" });
      return;
    }

    try {
      if (oneWayPrices.length > 0) {
        await postPrice({
          ageGroupId: tripParams?.ageGroupId,
          isTwoWay: false,
          applyToAll: false,
          lineId: tripParams?.lineId,
          tripId: tripParams?.tripId,
          defaultCurrencyId: tripParams?.defaultCurrencyId,
          prices: oneWayPrices
        });
      }

      if (roundTripPrices.length > 0) {
        await postPrice({
          ageGroupId: tripParams?.ageGroupId,
          isTwoWay: true,
          applyToAll: false,
          lineId: tripParams?.lineId,
          tripId: tripParams?.tripId,
          defaultCurrencyId: tripParams?.defaultCurrencyId,
          prices: roundTripPrices
        });
      }

      toast.success("Çmimi është publikuar me sukses.", {
        position: "top-right"
      });
    } catch {
      toast.error("Ndodhi një gabim gjatë krijimit.", { duration: 6000 });
    }
  };

  return (
    <FormWrapper
      pricing
      title="Cakto çmimet"
      onSubmit={handleSubmit(onSubmit)}
      className="max-w-full shadow-none border-none md:h-screen md:-mb-7"
      showSaveBtn={can}
    >
      {rows.length === 0 ? (
        <div className="w-full bg-gray-100 h-14 rounded-md justify-center overflow-auto">
          <p className="text-gray-500 text-center p-4">
            Ende nuk keni zgjedhur një linjë dhe orar...
          </p>
        </div>
      ) : (
        <div className="max-w-screen-sm md:max-w-full overflow-auto h-96 border tableDiv rounded-md">
          <table className="overflow-auto whitespace-nowrap p-0 relative w-full rounded-t-md">
            <thead className="sticky top-0 bg-white z-10 rounded-t-md">
              <tr>
                <th className="border-b text-left px-5 py-3 font-normal text-gray-600 text-xs">
                  Linja
                </th>
                <th className="border-b text-left px-5 py-3 font-normal text-gray-600 text-xs">
                  Çmimet
                </th>
                <th className="border-b text-left px-5 py-3 font-normal text-gray-600 text-xs">
                  Çmimi një drejtim ({currencyLabel})
                </th>
                <th className="border-b text-left px-5 py-3 font-normal text-gray-600 text-xs">
                  Çmimi vajtje-ardhje ({currencyLabel})
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={`${row.departure.id}-${row.destination.id}`}
                  className="hover:bg-gray-50 border-b"
                >
                  <td className="px-5 py-3 text-sm text-tableColor">
                    {row.departure.stationName.replace("Stacioni ", "")} -{" "}
                    {row.destination.stationName.replace("Stacioni ", "")}
                  </td>
                  <td className="px-5 py-3 text-sm text-tableColor">
                    <span className="flex items-center">
                      {row.oneWayPrice === 0 && row.roundTripPrice === 0 ? (
                        <span className="text-neutral">Nuk ka çmim</span>
                      ) : (
                        <>
                          {row.oneWayPrice > 0 && (
                            <span className="flex items-center justify-center mr-4">
                              <ArrowLongUpIcon className="w-4 h-4 text-neutral" />{" "}
                              {row.oneWayPrice}
                              {currencyLabel}
                            </span>
                          )}
                          {row.roundTripPrice > 0 && (
                            <span className="flex items-center justify-center">
                              <ArrowsUpDownIcon className="w-4 h-4 text-neutral mr-1" />{" "}
                              {row.roundTripPrice}
                              {currencyLabel}
                            </span>
                          )}
                        </>
                      )}
                    </span>
                  </td>
                  <td className="px-5 py-2">
                    <span
                      className={cx([
                        "flex items-center",
                        !can && "cursor-not-allowed bg-gray-200 rounded"
                      ])}
                    >
                      <Input
                        type="number"
                        {...register(`prices.${i}.oneWayPrice`, {
                          valueAsNumber: true
                        })}
                        defaultValue={row.oneWayPrice || undefined}
                        border={"border"}
                        disabled={!can}
                        placeholder="0.00"
                        step="any"
                      />
                    </span>
                    <input
                      type="hidden"
                      defaultValue={tripParams.tripId}
                      {...register(`prices.${i}.tripId`, {
                        valueAsNumber: true
                      })}
                    />
                    <input
                      type="hidden"
                      defaultValue={tripParams.ageGroupId}
                      {...register(`prices.${i}.ageGroupId`, {
                        valueAsNumber: true
                      })}
                    />
                    <input
                      type="hidden"
                      defaultValue={row.departure.id}
                      {...register(`prices.${i}.stationFrom`, {
                        valueAsNumber: true
                      })}
                    />
                    <input
                      type="hidden"
                      defaultValue={row.destination.id}
                      {...register(`prices.${i}.stationTo`, {
                        valueAsNumber: true
                      })}
                    />
                    <input
                      type="hidden"
                      defaultValue={row.oneWayPriceId}
                      {...register(`prices.${i}.oneWayPriceId`, {
                        valueAsNumber: true
                      })}
                    />
                    <input
                      type="hidden"
                      defaultValue={row.roundTripPriceId}
                      {...register(`prices.${i}.roundTripPriceId`, {
                        valueAsNumber: true
                      })}
                    />
                  </td>
                  <td className="px-5 py-2">
                    <span
                      className={cx([
                        "flex items-center",
                        !can && "cursor-not-allowed bg-gray-200 rounded"
                      ])}
                    >
                      <Input
                        type="number"
                        {...register(`prices.${i}.roundTripPrice`, {
                          valueAsNumber: true
                        })}
                        defaultValue={row.roundTripPrice || undefined}
                        border={"border"}
                        disabled={!can}
                        placeholder="0.00"
                        step="any"
                      />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </FormWrapper>
  );
};

export default PricingTable;
